/**
 * @file authApi.ts
 * @description RTK Query API slice for authentication endpoints.
 */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { PublicUserProfile } from '@forex-builder/shared-types';
import type { RootState } from '../../../app/store.js';

export interface AuthResponse {
  success: boolean;
  message?: string;
  data: {
    user: PublicUserProfile;
    accessToken: string;
  };
}

export interface RegisterRequest {
  name: string;
  email: string;
  password?: string;
}

export interface LoginRequest {
  email: string;
  password?: string;
}

export interface SendOtpRequest {
  email: string;
  name?: string;
}

export interface SendOtpResponse {
  success: boolean;
  message: string;
  data?: {
    email: string;
  };
}

export interface VerifyOtpRequest {
  email: string;
  code: string;
  name?: string;
}

import { createBaseQueryWithReauth } from '../../../shared/api/baseQueryWithReauth.js';

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: createBaseQueryWithReauth('/api/v1/auth'),
  endpoints: (builder) => ({
    sendOtp: builder.mutation<SendOtpResponse, SendOtpRequest>({
      query: (body) => ({
        url: '/send-otp',
        method: 'POST',
        body,
      }),
    }),
    verifyOtp: builder.mutation<AuthResponse, VerifyOtpRequest>({
      query: (body) => ({
        url: '/verify-otp',
        method: 'POST',
        body,
      }),
    }),
    register: builder.mutation<AuthResponse, RegisterRequest>({
      query: (credentials) => ({
        url: '/register',
        method: 'POST',
        body: credentials,
      }),
    }),
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/login',
        method: 'POST',
        body: credentials,
      }),
    }),
    refresh: builder.mutation<AuthResponse, void>({
      query: () => ({
        url: '/refresh',
        method: 'POST',
      }),
    }),
    logout: builder.mutation<{ success: boolean }, void>({
      query: () => ({
        url: '/logout',
        method: 'POST',
      }),
    }),
    getCurrentUser: builder.query<{ success: boolean; data: { user: PublicUserProfile } }, void>({
      query: () => '/me',
    }),
    updateProfile: builder.mutation<
      { success: boolean; message: string; data: { user: PublicUserProfile } },
      { name?: string; baseCurrency?: string }
    >({
      query: (body) => ({
        url: '/profile',
        method: 'PUT',
        body,
      }),
    }),
  }),
});

export const {
  useSendOtpMutation,
  useVerifyOtpMutation,
  useRegisterMutation,
  useLoginMutation,
  useRefreshMutation,
  useLogoutMutation,
  useGetCurrentUserQuery,
  useUpdateProfileMutation,
} = authApi;
